import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Sparkle, Clock } from '@phosphor-icons/react';
import { ProgressStepper } from '@/components/ProgressStepper';
import { ConfettiBurst } from '@/components/ConfettiBurst';

const NOVA_LOGO = "https://customer-assets.emergentagent.com/job_nova-fit-meals/artifacts/8r5uywp4_Nova%20Foods.png";

/**
 * Premium success screen for catering order.
 * Props:
 *  - confirmation: { request_id, subtotal, gst, total }
 *  - email: string (customer email displayed for confirmation)
 *  - onGoHome: () => void
 *  - onLogin: () => void
 */
export const CateringConfirmation = ({ confirmation, email, onGoHome, onLogin }) => {
  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#34D399]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#1A4331]/10 rounded-full blur-3xl" />
      </div>

      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-2xl border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={onGoHome} className="flex items-center gap-2 font-heading text-2xl font-semibold text-[#1A4331]">
            <img src={NOVA_LOGO} alt="Nova Foods" className="h-10 w-10 rounded-lg object-cover" />
            Nova Foods
          </button>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-6 relative z-10">
        <div className="bg-white/70 backdrop-blur-xl border border-stone-200/60 rounded-2xl p-4 shadow-sm">
          <ProgressStepper steps={['Cart', 'Details', 'Review', 'Success']} current={4} />
        </div>
      </section>

      <main className="flex-1 flex items-center justify-center px-6 py-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl w-full text-center relative"
          data-testid="catering-confirmation"
        >
          <ConfettiBurst show count={48} />

          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 180, damping: 14 }}
            className="w-28 h-28 bg-gradient-to-br from-[#34D399] to-[#10B981] rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-[#10B981]/30 relative z-10"
          >
            <CheckCircle size={64} weight="fill" className="text-white" />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="inline-flex items-center gap-2 bg-[#1A4331]/10 text-[#1A4331] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] mb-5">
            <Sparkle size={14} weight="fill" /> Order Confirmed
          </motion.div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4">
            Thank you!
          </h1>
          <p className="text-base leading-relaxed text-stone-600 mb-10 max-w-lg mx-auto">
            Your catering request has been received. We&apos;ve emailed the full details to <span className="font-semibold text-[#1A4331]">{email}</span> and our team will confirm shortly.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="bg-white border border-stone-100 rounded-3xl p-8 mb-10 shadow-xl shadow-stone-200/40"
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">Your Request ID</p>
            <p className="text-3xl font-bold text-[#1A4331] mb-6 font-heading tracking-tight" data-testid="catering-request-id">
              {confirmation.request_id}
            </p>

            <div className="grid grid-cols-3 gap-3 text-sm mb-6">
              <div className="bg-stone-50 rounded-2xl p-4">
                <p className="text-stone-500 text-xs mb-1">Subtotal</p>
                <p className="font-bold text-[#1C1917]">₹{confirmation.subtotal?.toLocaleString()}</p>
              </div>
              <div className="bg-stone-50 rounded-2xl p-4">
                <p className="text-stone-500 text-xs mb-1">GST (5%)</p>
                <p className="font-bold text-[#1C1917]">₹{confirmation.gst?.toLocaleString()}</p>
              </div>
              <div className="bg-gradient-to-br from-[#1A4331] to-[#2A5A44] rounded-2xl p-4 text-white">
                <p className="text-white/70 text-xs mb-1">Total</p>
                <p className="font-bold">₹{confirmation.total?.toLocaleString()}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 bg-[#FEF3C7] text-[#92400E] px-4 py-2 rounded-full text-sm font-medium">
              <Clock size={16} weight="duotone" /> Pending Approval
            </div>
          </motion.div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={onLogin}
              className="bg-[#1A4331] text-white px-8 py-3.5 rounded-full font-medium hover:bg-[#2A5A44] transition-all shadow-lg shadow-[#1A4331]/20 hover:shadow-xl hover:-translate-y-0.5"
              data-testid="login-track-btn"
            >
              Login to Track Order
            </button>
            <button
              onClick={onGoHome}
              className="border border-stone-300 bg-white text-stone-700 px-8 py-3.5 rounded-full font-medium hover:bg-stone-50 transition-all"
            >
              Back to Home
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default CateringConfirmation;
